import React from 'react';
import { X } from 'lucide-react';
import { C } from '../../theme.js';
import { IconButton, Select, TextInput } from '../../components/Primitives.jsx';
import { OPERATORS } from '../../data.js';
import { ColSelect } from './ColSelect.jsx';
import { SourceSelect } from './SourceSelect.jsx';
import { sourceColumns } from './sourceColumns.js';

// Mirrors engine/conditions.py: these operators can't take a column on the
// right-hand side; blank/notblank ignore the right side entirely.
const LITERAL_ONLY_OPS = ['matches', 'in', 'nin', 'in_source', 'nin_source'];
const SOURCE_OPS = ['in_source', 'nin_source'];
const COMPARE_OPTIONS = [
  { value: 'text', label: 'As text' },
  { value: 'number', label: 'As number' },
  { value: 'auto', label: 'Auto' },
];
const VALUE_MODE_OPTIONS = [
  { value: 'literal', label: 'Value' },
  { value: 'column', label: 'Column' },
];

// ── ConditionRows ────────────────────────────────────────────────────────
// The column / operator / value rows shared by Filter and Case When. Two
// looks, kept pixel-identical to what each inspector rendered before:
//   variant="card"  (Filter)    — bordered card per row, value box hidden
//                                  for operators that take none, per-row
//                                  remove button.
//   variant="plain" (Case When) — bare row, value box always shown. The
//                                  remove button appears only when the
//                                  caller passes onRemove.
// Optional condition keys (value_mode, compare) are only written once the
// user touches them; absent means literal / text, as the engine reads it.
// Exists-in-source operators swap the value box for a source selector + a
// source-column picker (`sourceCtx` carries what SourceSelect/sourceColumns
// need); between shows two bounds, each following the Value/Column toggle.
export function ConditionRows({ conditions, columns, onUpdate, onRemove, variant = 'card', sourceCtx }) {
  const card = variant === 'card';
  return (
    <>
      {conditions.map((cd, i) => {
        const op = OPERATORS.find(o => o.id === cd.op);
        const needsValue = card ? !!op?.needsValue : (op ? op.needsValue : true);
        const isSourceOp = SOURCE_OPS.includes(cd.op);
        const isBetween = cd.op === 'between' || cd.op === 'nbetween';
        const literalOnly = LITERAL_ONLY_OPS.includes(cd.op);
        const isColumn = cd.value_mode === 'column' && !literalOnly;
        const showModeToggle = needsValue && !literalOnly;
        const showCompare = cd.op === 'eq' || cd.op === 'neq';
        return (
          <div key={i} style={{
            display: 'flex', gap: 6, flexWrap: 'wrap',
            ...(card ? {
              alignItems: 'stretch', marginBottom: 8,
              padding: 8, borderRadius: 8,
              background: C.input, border: `1px solid ${C.border}`,
            } : { marginBottom: 6 }),
          }}>
            <Select
              value={cd.column}
              onChange={(v) => onUpdate(i, { column: v })}
              options={columns.map(col => ({ value: col.name, label: col.name }))}
              style={{ flex: '0 0 100px' }}
            />
            <Select
              value={cd.op}
              onChange={(v) => onUpdate(i, LITERAL_ONLY_OPS.includes(v) && cd.value_mode === 'column'
                ? { op: v, value_mode: 'literal', value: '' }
                : SOURCE_OPS.includes(v) && !SOURCE_OPS.includes(cd.op)
                  ? { op: v, compare: 'text', source: cd.source ?? '', source_column: cd.source_column ?? '' }
                  : { op: v })}
              options={OPERATORS.map(o => ({ value: o.id, label: o.label }))}
              style={{ flex: card ? '0 0 110px' : '0 0 100px' }}
            />
            {showModeToggle && (
              <Select
                value={isColumn ? 'column' : 'literal'}
                onChange={(v) => onUpdate(i, { value_mode: v, value: '' })}
                options={VALUE_MODE_OPTIONS}
                style={{ flex: '0 0 78px' }}
              />
            )}
            {isSourceOp && (
              <>
                <div style={{ flex: '1 1 150px', minWidth: 140 }}>
                  <SourceSelect
                    value={cd.source}
                    onChange={(v) => onUpdate(i, { source: v, source_column: '' })}
                    secondaryDatasets={sourceCtx?.secondaryDatasets}
                    sheets={sourceCtx?.sheets}
                    pipeline={sourceCtx?.pipeline}
                    currentStep={sourceCtx?.currentStep}
                    onBrowse={sourceCtx?.onBrowse}
                    buildTargetName={sourceCtx?.buildTargetName}
                    placeholder="— pick a source —"
                  />
                </div>
                <ColSelect
                  value={cd.source_column}
                  onChange={(v) => onUpdate(i, { source_column: v })}
                  columns={cd.source === '__self__' ? columns : (sourceColumns(cd.source, sourceCtx || {}) || [])}
                  placeholder="— source column —"
                  style={{ flex: '1 1 110px', minWidth: 100 }}
                />
              </>
            )}
            {needsValue && isBetween && (isColumn ? (
              <>
                <ColSelect value={cd.value} onChange={(v) => onUpdate(i, { value: v })} columns={columns} placeholder="— from column —" style={{ flex: 1, minWidth: 90 }} />
                <span style={{ color: C.fg5, fontSize: '.74rem', alignSelf: 'center' }}>and</span>
                <ColSelect value={cd.value2} onChange={(v) => onUpdate(i, { value2: v })} columns={columns} placeholder="— to column —" style={{ flex: 1, minWidth: 90 }} />
              </>
            ) : (
              <>
                <TextInput value={cd.value} onChange={(v) => onUpdate(i, { value: v })} placeholder="from" style={{ flex: 1, minWidth: 50 }} />
                <span style={{ color: C.fg5, fontSize: '.74rem', alignSelf: 'center' }}>and</span>
                <TextInput value={cd.value2} onChange={(v) => onUpdate(i, { value2: v })} placeholder="to" style={{ flex: 1, minWidth: 50 }} />
              </>
            ))}
            {needsValue && !isBetween && (isColumn ? (
              <ColSelect
                value={cd.value}
                onChange={(v) => onUpdate(i, { value: v })}
                columns={columns}
                style={{ flex: 1, minWidth: 90 }}
              />
            ) : (
              <TextInput
                value={cd.value}
                onChange={(v) => onUpdate(i, { value: v })}
                placeholder={cd.op === 'in' || cd.op === 'nin' ? 'EUR, GBP, USD' : 'value'}
                style={card ? { flex: 1, minWidth: 60 } : undefined}
              />
            ))}
            {showCompare && (
              <Select
                value={cd.compare || 'text'}
                onChange={(v) => onUpdate(i, { compare: v })}
                options={COMPARE_OPTIONS}
                style={{ flex: '0 0 96px' }}
              />
            )}
            {onRemove && (
              <IconButton title="Remove condition" onClick={() => onRemove(i)} style={{ flex: '0 0 30px' }}><X size={14} /></IconButton>
            )}
          </div>
        );
      })}
    </>
  );
}
